<template>
  <section class="sec_product-detail">
    <PartsProductDetailHead :name="product.name" :image="fvImage" :sp-image="fvSpImage">
      <PartsProductDetailNote
        :title="product.topCatchphrase"
        :body="descriptionLines"
        :amount-label="content.amount?.label"
        :amount-value="content.amount?.value ?? ''"
      />
    </PartsProductDetailHead>

    <PartsLayoutContainer>
      <div class="product-buy-pannel">
        <PartsProductBuyGallery :images="galleryImages" :alt="product.name" />
        <OrganismsProductsDetailBuyForms :product="product" :content="content" />
      </div>

      <!-- エパゴールド3種の比較・相互送客（`549` `558` `559` のみ・決定#62） -->
      <PartsProductVarietiesArea
        v-if="varieties"
        :title="varieties.title"
        :head-image="varieties.headImage"
        :labels="varieties.labels"
        :items="varieties.items"
        :note="varieties.note"
      />

      <PartsProductComparePrice
        v-if="content.comparePrice || showRegularCourse"
        :rows="content.comparePrice?.rows ?? []"
        :unit="content.comparePrice?.unit"
        :lead="content.comparePrice?.lead"
      >
        <PartsProductRegularCourseNote
          v-if="showRegularCourse && content.regularCourse"
          :min-count="content.regularCourse.minCount"
          :changeable-from="content.regularCourse.changeableFrom"
          :cycles="regularCycles"
          :unit="content.regularCourse.unit"
          :unit-note="content.regularCourse.unitNote"
          detail-href="/regular"
        />
      </PartsProductComparePrice>

      <PartsProductInfoTabs :tabs="tabs">
        <template #features>
          <!--
            **本文を隠す商品は `pfb-wrap` で囲む**（第8便の方針変更・第9便で全38商品に）。
            支給CSS の `.pfb-wrap`（`renewal/style.css:6980`）は
            **1列＋`max-width: 700px` の中央寄せ**（第8便は2列だったが第9便で1列になった）。

            **本文を出す商品には器を被せない。** SP の
            `.product-feature-block:nth-child(odd) { flex-direction: column-reverse }`
            （`同:7511`）は親の中での順番で効くので、器を1枚挟むと画像と本文の上下が入れ替わる。
            → `docs/design-renewal/PRODUCT-FEATURE.md`
          -->
          <div v-if="featuresGrid" class="pfb-wrap">
            <PartsProductFeatureBlock
              v-for="(block, index) in featureBlocks"
              :key="index"
              v-bind="block"
            />
          </div>
          <template v-else>
            <PartsProductFeatureBlock
              v-for="(block, index) in featureBlocks"
              :key="index"
              v-bind="block"
            />
          </template>
        </template>

        <template #faq>
          <PartsProductFaqList :items="content.faq ?? []" :footer-link="FAQ_FOOTER_LINK" />
        </template>

        <template #reviews>
          <PartsProductReviewSlider :cards="content.reviews ?? []" />
        </template>

        <template #spec>
          <PartsProductSpecList :groups="specGroups" />
        </template>
      </PartsProductInfoTabs>
    </PartsLayoutContainer>
  </section>
</template>

<script setup lang="ts">
/**
 * 商品詳細ページの本体（支給デザインの `sec_product-detail`）。
 * **API の `product` と商品別 const の2つを突き合わせて14部品へ配る**のがこの層の仕事で、
 * カートへの結線は `buy-forms.vue` に閉じている。
 *
 * **ブロックの出し分けは商品別 const のキーの有無で決める**（決定#58）。静的マップは持たない。
 * タブは `商品特徴 → FAQ → ご愛用者様レビュー → 成分・商品情報` の固定順から、キーがあるものだけを出す
 * （支給38本の実測は3パターンだけで、`成分・商品情報` は常に最後）。
 *
 * **API と const の担当は重ねない**（決定#5）。商品名・リード文の見出し・価格・数量・配送間隔は API、
 * 成分・FAQ・レビュー・商品特徴・価格表・画像は const。**【商品名】【商品情報】は const に持たず
 * ここで API から組み立てて `product-spec-list` の先頭に足す**（37/38 がこの2項目で始まる）。
 */
import type { ProductType, ProductDetailContentType } from '@/types/domains/index';
import { getProductImageUrl } from '@/utils/ImageHelper';

type Props = {
  product: ProductType.Product;
  content: ProductDetailContentType.ProductDetailContent;
};

const props = defineProps<Props>();

/** 支給の FAQ 一覧の下にある `/faq` への導線（帯とボタンの2本・同じ飛び先） */
const FAQ_FOOTER_LINK = {
  href: '/faq',
  pcLabel: 'その他のお買い物に関するよくあるご質問はこちらからご確認いただけます　>>',
  spLabel: 'その他のお買い物に関するよくあるご質問はこちらからご確認いただけます　>>',
  buttonLabel: 'よくあるご質問へ',
};

/**
 * 支給のタブ文言とパネル見出し。どちらも支給の固定文言で、**両者は一致しない**
 * （タブ「ご愛用者様レビュー」に対しパネル見出しは「お客様レビュー」、
 * タブ「FAQ」に対し見出しは「FAQ〈商品に関するよくあるご質問〉」）。
 * タブ側の `<br>` はタブ幅に合わせた改行位置の指定（§3.3.1 #10）。
 */
const TABS: Record<string, { label: string; panelTitle: string }> = {
  features: { label: '商品特徴', panelTitle: '商品特徴' },
  faq: { label: 'FAQ', panelTitle: 'FAQ〈商品に関するよくあるご質問〉' },
  reviews: { label: 'ご愛用者様<br class="tb-only" />レビュー', panelTitle: 'お客様レビュー' },
  spec: { label: '成分・商品<br class="sp-only" />情報', panelTitle: '成分・商品情報' },
};

/**
 * const の画像はファイル名で持つ（決定#59）。複数商品で共有する20点だけは
 * `/image/product/common/` へのフルパスなので、そのまま使う（決定#64）。
 */
const imageUrl = (file: string): string =>
  file.startsWith('/') ? file : getProductImageUrl(props.product.code, '', file);

/**
 * 商品特徴のブロック。**器の有無で2箇所から出すので、props はここで1本にまとめる**（決定#5）。
 * `textHidden` は商品ごとの方針（`featuresImageOnly`）をそのまま渡す。
 */
const featureBlocks = computed(() =>
  (props.content.features ?? []).map((block) => ({
    heading: block.heading,
    body: block.body,
    image: imageUrl(block.image),
    reverse: block.reverse,
    textHidden: props.content.featuresImageOnly === true,
  }))
);

/**
 * `pfb-wrap` で囲むか。**本文非表示の商品すべて**（第9便の実測で23/23）。
 * 第8便では1ブロックの `710` `QBM01` に付いていなかったが第9便で付いたので、
 * ブロック数からの導出をやめて `featuresImageOnly` と同値にした。
 * `pfb-wrap` は**1列＋`max-width: 700px` の中央寄せ**（`renewal/style.css:6980`）。
 */
const featuresGrid = computed<boolean>(() => props.content.featuresImageOnly === true);

const fvImage = computed<string>(() => imageUrl(props.content.fv.pc));
const fvSpImage = computed<string>(() => imageUrl(props.content.fv.sp));
const galleryImages = computed<string[]>(() => props.content.gallery.map(imageUrl));

/**
 * リード文の本文。**API の `description` が正**だが 22/38 でしか返らないため、
 * 残る16商品は const から取る（決定#5・`PRODUCT-API.md` §2）。
 * `annotation` は `525` の「※対象年齢6歳以上」だけが値を持ち、支給も本文の末尾に置いている。
 */
const descriptionLines = computed<string[]>(() => {
  const lines: string[] = props.product.description
    ? props.product.description.split(/\r?\n/).map((line) => line.trim())
    : (props.content.description ?? []);

  return [...lines, ...(props.product.annotation ? [props.product.annotation] : [])];
});

/**
 * 「定期コースについて」を出すか。**定期の購入枠と同じ条件で出す**
 * （`buy-forms.vue` の `regularBox`）。const に `regularCourse` があるだけで出すと、
 * **API がまだ定期を返していない商品で「買えない定期」の説明だけが出る**。
 * `L61` `L62` は管理画面で定期を登録するまで `regularCode` が `null`（#1398）。
 * `NST` は商品実体を持たず `regularCode` が `null` のまま `upcell` で定期枠を出す（決定#69）。
 */
const showRegularCourse = computed<boolean>(() =>
  Boolean(
    props.content.regularCourse && (props.product.regularCode || props.content.regularBox?.upcell)
  )
);

/**
 * 「定期コースについて」に出す配送間隔。threshold を持つ7商品は数量帯ごとに変わるので、
 * 説明文には**全帯の和集合**を渡す（数量1で `[1]`／数量2以上で `[1,2]` → `[1,2]`）。
 *
 * **API から作れるのは既存 `regularAnnotation.vue` を持つ19商品で全数確認済み**
 * （3袋定期の `L61` `L62` を足して21商品・#1398）
 * （併記13／60日のみ4／30日のみ2 が完全一致）。**支給デザインは20本すべて併記なので、
 * `510` `512` `513` `516` `525` `558` の6商品は支給が誤り**であり API 側を採る（決定#47）。
 * `NST` だけは API が配送間隔を返さないので const から取る（決定#69）。
 */
const regularCycles = computed<number[]>(() => {
  const fromConst = props.content.regularCourse?.cycles;
  if (fromConst?.length) return fromConst;

  const thresholds: ProductType.QuantityThresholdCode[] = props.product.quantityThresholdCodes;
  if (thresholds.length) {
    return [...new Set(thresholds.flatMap((threshold) => threshold.availableCycles))].sort(
      (a, b) => a - b
    );
  }

  return props.product.regularCycle ?? [];
});

const varieties = computed(() => {
  const source = props.content.varieties;
  if (!source) return undefined;

  return {
    title: { ...source.title, pc: imageUrl(source.title.pc), sp: imageUrl(source.title.sp) },
    headImage: source.headImage ? imageUrl(source.headImage) : '',
    labels: source.labels ?? [],
    items: source.items.map((item) => ({
      ...item,
      image: imageUrl(item.image),
      spImage: imageUrl(item.spImage),
      ...(item.badge ? { badge: imageUrl(item.badge) } : {}),
    })),
    note: source.note ?? [],
  };
});

const tabs = computed<{ key: string; label: string; panelTitle: string }[]>(() =>
  (['features', 'faq', 'reviews', 'spec'] as const)
    .filter((key) => key === 'spec' || (props.content[key]?.length ?? 0) > 0)
    .map((key) => ({ key, ...TABS[key] }))
);

/**
 * 成分・商品情報。**【商品名】【商品情報】は const に持たず API から組み立てる**（決定#5）。
 * 支給37/38 がこの2項目で始まり、どちらも `name` / `topCatchphrase` / `description` そのもの。
 * `K41`〜`K45` だけ小見出し付きのグループに分かれるので、その場合は独立したグループとして先頭に置く。
 */
const specGroups = computed<ProductDetailContentType.ProductSpecGroup[]>(() => {
  const head: ProductDetailContentType.ProductSpecItem[] = [
    { label: '商品名', body: [props.product.name] },
    { label: '商品情報', body: [props.product.topCatchphrase, ...descriptionLines.value] },
  ];
  const [first, ...rest] = props.content.spec;

  return first && !first.heading
    ? [{ items: [...head, ...first.items] }, ...rest]
    : [{ items: head }, ...props.content.spec];
});
</script>
